// Implements: F-GEN-033, F-UIX-009
/**
 * What the drawer's diagram tabs read, besides the draw itself. A module may not reach into
 * `versions` or `conversation`, so these read the same routes under the same shared cache keys
 * (`versionDetailKey`, `versionTreeKey`), with the same answer shape as those modules give them.
 *
 * - `GET /mockups/{id}/versions/{version_id}`: which pieces a version holds (which diagrams).
 * - `GET /mockups/{id}/versions/tree`: the versions of the mockup, for the version picker.
 * - `GET /mockups/{id}/conversation/messages`: the mockup's brief, its first message (P6-2).
 */

import { ApiError, UNKNOWN_ERROR_CODE } from "../../../shared/api/ApiError";
import { API_BASE } from "../../../shared/api/apiBase";
import { conversationPath } from "../../../shared/api/conversationPath";
import { httpRequest } from "../../../shared/api/httpClient";
import type { MessagesPageView, VersionDetailView, VersionTreeNode, VersionTreeView } from "../../../types/api";

function mockupPath(mockupId: string): string {
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}`;
}

export async function fetchVersionArtifacts(mockupId: string, versionId: string): Promise<VersionDetailView> {
  const body = await httpRequest<VersionDetailView>(`${mockupPath(mockupId)}/versions/${encodeURIComponent(versionId)}`);
  if (body === undefined) {
    throw new ApiError(200, UNKNOWN_ERROR_CODE, "GET /mockups/{id}/versions/{version_id} answered with no body", new Headers());
  }
  return body;
}

/** The nodes of the tree, as the `versions` module caches them under `versionTreeKey`. */
export async function fetchVersionNodes(mockupId: string): Promise<readonly VersionTreeNode[]> {
  const tree = await httpRequest<Partial<VersionTreeView>>(`${mockupPath(mockupId)}/versions/tree`);
  return tree?.nodes ?? [];
}

/** The mockup's brief: the text of the person's first message, or `""` when there is none. */
export async function fetchMockupBrief(mockupId: string): Promise<string> {
  const page = await httpRequest<Partial<MessagesPageView>>(`${conversationPath(mockupId)}/messages?limit=50`);
  const first = (page?.items ?? []).find((message) => message.author === "person" && message.kind === "input");
  return first?.body.trim() ?? "";
}
